import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, signOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { apiService } from '../services/api';

interface User {
  username: string;
  displayName: string; // Human-readable display name
  email: string;
  sub: string; // Cognito user UUID
  groups: string[];
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const userAttributes = await fetchUserAttributes();
      
      console.log('Current user:', currentUser);
      console.log('Session:', session);
      console.log('User attributes:', userAttributes);
      
      // Extract groups from JWT token
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
      const isAdmin = groups.includes('admin');
      
      // Extract sub (user UUID) from JWT token
      const sub = session.tokens?.idToken?.payload?.sub as string || currentUser.userId || '';

      // Create display name with fallback logic
      const displayName = userAttributes.name || 
                          userAttributes.email || 
                          currentUser.signInDetails?.loginId || 
                          currentUser.username || 
                          'User';

      const userData = {
        username: currentUser.username,
        displayName,
        email: currentUser.signInDetails?.loginId || currentUser.username || '',
        sub,
        groups,
        isAdmin
      };

      console.log('Setting user data:', userData);
      setUser(userData);
    } catch (error) {
      console.log('No authenticated user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear the ApiService token cache to prevent cross-user data contamination
      apiService.clearTokenCache();
      
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    await fetchUser();
  };

  useEffect(() => {
    fetchUser();

    // Listen for authentication events
    const hubListener = (data: any) => {
      const { payload } = data;
      console.log('Auth Hub event:', payload);
      
      switch (payload.event) {
        case 'signedIn':
        case 'signUp':
        case 'autoSignIn':
          console.log('User signed in, refreshing user data');
          fetchUser();
          break;
        case 'signedOut':
          console.log('User signed out');
          setUser(null);
          break;
        case 'tokenRefresh':
          console.log('Token refreshed, updating user data');
          fetchUser();
          break;
        default:
          break;
      }
    };

    const unsubscribe = Hub.listen('auth', hubListener);

    return () => {
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    signOut: handleSignOut,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};